import { FC, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Container } from '../Container';
import { useAuth } from '../../../contexts/AuthContext';
import { UserMenu } from './UserMenu';
import { MobileMenu } from './MobileMenu';
import { buttonVariants } from '../../ui/Button';
import { ThemeToggle } from '../../theme';

export interface HeaderProps {
  className?: string;
}

interface NavLink {
  to: string;
  label: string;
}

const navLinks: NavLink[] = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/book', label: 'Book Now' },
];

/**
 * Header component - Global navigation header
 * Sticky header with logo, navigation links, and user menu
 */
export const Header: FC<HeaderProps> = ({ className }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const handleCloseMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  // Track scroll position for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActiveLink = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50',
          'transition-all duration-300',
          isScrolled
            ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm dark:shadow-slate-800/20'
            : 'bg-transparent',
          className
        )}
      >
        <Container>
          <nav className="flex items-center justify-between h-16" aria-label="Main navigation">
            {/* Logo */}
            <Link
              to="/"
              className="text-xl font-bold text-gray-900 dark:text-slate-100 transition-colors"
            >
              Massa<span className="text-primary-600 dark:text-primary-400">therapy</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={cn(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActiveLink(link.to)
                      ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30'
                      : 'text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100 hover:bg-gray-100 dark:hover:bg-slate-800'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop Auth/User Menu */}
            <div className="hidden lg:flex items-center gap-3">
              <ThemeToggle />
              {isLoading ? (
                // Loading skeleton
                <div className="flex items-center gap-3">
                  <div className="w-20 h-9 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse" />
                  <div className="w-24 h-9 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse" />
                </div>
              ) : isAuthenticated ? (
                <UserMenu />
              ) : (
                <>
                  <Link
                    to="/login"
                    className={cn(
                      buttonVariants({ variant: 'ghost', size: 'sm' }),
                      'text-gray-700 dark:text-slate-300'
                    )}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className={buttonVariants({ variant: 'primary', size: 'sm' })}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile: Theme Toggle + Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(true)}
                className={cn(
                  'p-2 rounded-lg',
                  'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                aria-label="Open menu"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </nav>
        </Container>
      </header>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={handleCloseMenu} />
    </>
  );
};

Header.displayName = 'Header';
